import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import './MyEventsPage.css';
import { log, logError } from '../../utils/logger';

const resolveImageUrl = (imagePath) => {
  if (!imagePath) return null;
  const clean = String(imagePath).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(clean)) return clean;
  const base = getApiBaseUrl();
  if (clean.startsWith('/')) return `${base}${clean}`;
  if (clean.startsWith('uploads/')) return `${base}/${clean}`;
  return `${base}/uploads/${clean}`;
};

const MyEventsPage = () => {
  const { user, token, isAuthenticated } = useAuth();
  const { notify } = useNotifications();
  const navigate = useNavigate();
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated || !user || !token) {
      log('⚠️ Usuario no autenticado, redirigiendo a login...');
      navigate('/login', { 
        state: { 
          from: '/my-events',
          message: 'Debes iniciar sesión para ver tus eventos'
        } 
      });
      return;
    }

    fetchUserEvents();
  }, [isAuthenticated, user, token, navigate]);

  const fetchUserEvents = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(buildApiUrl('/api/user/events'), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.status === 401) {
        navigate('/login', { 
          state: { 
            from: '/my-events',
            message: 'Tu sesión ha expirado. Por favor, inicia sesión nuevamente'
          } 
        });
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error al cargar eventos (${response.status})`);
      }

      const data = await response.json();
      setEvents(data.eventos || []);
    } catch (err) {
      logError('[MyEventsPage] Error cargando eventos:', err);
      setError(err.message || 'Error al cargar tus eventos');
      notify({
        message: err.message || 'Error al cargar tus eventos',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      notify({
        message: `${label} copiado al portapapeles`,
        type: 'success',
      });
    }).catch(() => {
      notify({
        message: `Error al copiar ${label}`,
        type: 'error',
      });
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="my-events-page">
        <div className="my-events-container">
          <div className="my-events-loading">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p>Cargando tus eventos...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-events-page">
        <div className="my-events-container">
          <div className="my-events-error">
            <i className="fas fa-exclamation-triangle" />
            <h2>Error al cargar eventos</h2>
            <p>{error}</p>
            <button 
              className="btn btn-primary"
              onClick={fetchUserEvents}
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="my-events-page">
        <div className="my-events-container">
          <div className="my-events-header">
            <h1>
              <i className="fas fa-calendar-alt" />
              Mis Eventos
            </h1>
          </div>
          
          <div className="my-events-empty">
            <div className="my-events-empty-icon">
              <i className="fas fa-calendar-times" />
            </div>
            <h2>Aún no tienes eventos</h2>
            <p>Explora nuestro catálogo y encuentra eventos que te interesen.</p>
            <button 
              className="btn btn-primary btn-lg"
              onClick={() => navigate('/events')}
            >
              <i className="fas fa-calendar-check" />
              Ver Catálogo de Eventos
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-events-page">
      <div className="my-events-container">
        <div className="my-events-header">
          <h1>
            <i className="fas fa-calendar-alt" />
            Mis Eventos
          </h1>
          <p className="my-events-subtitle">
            Gestiona tus eventos y accede a tus códigos y links
          </p>
        </div>

        <div className="my-events-events-wrapper">
          <div className="my-events-grid">
            {events.map((event) => {
            const detailRouteId = event.eventId ?? event.id;
            const imageUrl = resolveImageUrl(event.imagen);
            const isVirtual = event.formato === 'virtual';
            const isPresencial = event.formato === 'presencial';
            const hasCodes = event.codigos && event.codigos.length > 0;
            const primaryCode = hasCodes ? event.codigos[0] : null;

            return (
              <div key={`${event.eventId ?? event.id}-${event.id}`} className="my-events-card">
                <div className="my-events-card-image">
                  {imageUrl ? (
                    <img src={imageUrl} alt={event.titulo} />
                  ) : (
                    <div className="my-events-card-placeholder">
                      <i className="fas fa-calendar-alt" />
                    </div>
                  )}
                  <div className="my-events-card-badge">
                    {isVirtual ? (
                      <span className="badge-virtual">
                        <i className="fas fa-video" /> Virtual
                      </span>
                    ) : (
                      <span className="badge-presencial">
                        <i className="fas fa-map-marker-alt" /> Presencial
                      </span>
                    )}
                  </div>
                </div>

                <div className="my-events-card-content">
                  <h3 className="my-events-card-title">{event.titulo}</h3>
                  
                  {event.descripcionBreve && (
                    <p className="my-events-card-description">{event.descripcionBreve}</p>
                  )}

                  {event.acquiredAt && (
                    <div className="my-events-card-meta">
                      <i className="fas fa-calendar-check" />
                      <span>Adquirido: {formatDate(event.acquiredAt)}</span>
                    </div>
                  )}

                  {/* Código para eventos presenciales */}
                  {isPresencial && hasCodes && primaryCode && (
                    <div className="my-events-card-code">
                      <div className="my-events-code-header">
                        <label>
                          <i className="fas fa-ticket-alt" />
                          Código de Acceso
                        </label>
                        <button
                          className="btn-copy-code"
                          onClick={() => copyToClipboard(primaryCode.code, 'Código')}
                          title="Copiar código"
                        >
                          <i className="fas fa-copy" />
                        </button>
                      </div>
                      <div className="my-events-code-value">
                        <code>{primaryCode.code}</code>
                        {primaryCode.status === 'used' && (
                          <span className="code-status-used">
                            <i className="fas fa-check-circle" /> Usado
                          </span>
                        )}
                      </div>
                      {primaryCode.assignedAt && (
                        <small className="my-events-code-date">
                          Asignado: {formatDate(primaryCode.assignedAt)}
                        </small>
                      )}
                    </div>
                  )}

                  {/* Link para eventos virtuales */}
                  {isVirtual && event.linkVirtual && (
                    <div className="my-events-card-link">
                      <div className="my-events-link-header">
                        <label>
                          <i className="fas fa-link" />
                          Link de Acceso Virtual
                        </label>
                        <button
                          className="btn-copy-link"
                          onClick={() => copyToClipboard(event.linkVirtual, 'Link')}
                          title="Copiar link"
                        >
                          <i className="fas fa-copy" />
                        </button>
                      </div>
                      <a
                        href={event.linkVirtual}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="my-events-link-value"
                      >
                        <i className="fas fa-external-link-alt" />
                        Acceder al Evento
                      </a>
                    </div>
                  )}

                  {/* Dirección para eventos presenciales */}
                  {isPresencial && event.direccion && (
                    <div className="my-events-card-address">
                      <label>
                        <i className="fas fa-map-marker-alt" />
                        Dirección
                      </label>
                      <p className="my-events-address-value">{event.direccion}</p>
                      {event.linkPresencial && (
                        <a
                          href={event.linkPresencial}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-primary btn-sm"
                        >
                          <i className="fas fa-map" />
                          Ver en Google Maps
                        </a>
                      )}
                    </div>
                  )}

                  {/* Días del evento */}
                  {event.diaseventos && Array.isArray(event.diaseventos) && event.diaseventos.length > 0 && (
                    <div className="my-events-card-dates">
                      <label>
                        <i className="fas fa-calendar-day" />
                        Fechas del Evento
                      </label>
                      <ul className="my-events-dates-list">
                        {event.diaseventos.slice(0, 3).map((dia, idx) => (
                          <li key={idx}>
                            <i className="fas fa-calendar" />
                            {dia.fecha && formatDate(dia.fecha)}
                            {dia.titulo && ` - ${dia.titulo}`}
                            {dia.horaInicio && ` (${dia.horaInicio})`}
                          </li>
                        ))}
                        {event.diaseventos.length > 3 && (
                          <li className="more-dates">
                            +{event.diaseventos.length - 3} fecha(s) más
                          </li>
                        )}
                      </ul>
                    </div>
                  )}

                  {detailRouteId != null ? (
                    <Link className="my-events-card-cta" to={`/my-events/${detailRouteId}`}>
                      <i className="fas fa-arrow-right" aria-hidden />
                      Ver detalles del evento
                    </Link>
                  ) : (
                    <span className="my-events-card-cta my-events-card-cta--disabled">
                      <i className="fas fa-arrow-right" aria-hidden />
                      Ver detalles del evento
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyEventsPage;

